import React, { Suspense } from 'react'
import { Streamdown } from '@phaserjs/streamdown-lite'
import {
  CheckIcon,
  ClipboardListIcon,
  EditIcon,
  Loader2Icon,
} from 'lucide-react'
import { createPortal } from 'react-dom'
import Markdown from 'react-markdown'
import rehypeRaw from 'rehype-raw'
import remarkBreaks from 'remark-breaks'
import remarkFrontmatter from 'remark-frontmatter'
import remarkGfm from 'remark-gfm'

import { useJobLog } from '@/components/hooks/use-job-log'
import {
  type FileData,
  type ResumeAlphaData,
} from '@/components/hooks/use-resume-tailor'
import {
  JobEntryDialog,
  type JobEntryFormData,
} from '@/components/job-log/job-entry-dialog'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'

import { cn } from '@/lib/utils'

import { ResumeDownloader } from './resume-downloader'

// Lazy load Monaco editor to avoid SSR issues - it's client-only
const MonacoEditor = React.lazy(() => import('@monaco-editor/react'))

export const ResultStage = (props: {
  data: ResumeAlphaData
  setData: React.Dispatch<React.SetStateAction<ResumeAlphaData>>
  isAnalyzing?: boolean
  isStreaming?: boolean
  onFinished?: (
    action:
      | 're-analyze'
      | 'restart-with-new-resume'
      | 'restart-with-old-resume',
    file: FileData,
  ) => void
}) => {
  const { data, setData, isStreaming } = props
  const { addApplication } = useJobLog()

  const [mode, setMode] = React.useState<'default' | 'edit'>('default')
  const [editText, setEditText] = React.useState(
    props.data.result?.resume || '',
  )
  const [downloadFilename] = React.useState('tailored-resume')
  const [logDialogOpen, setLogDialogOpen] = React.useState(false)

  const handleLogApplication = React.useCallback(
    (formData: JobEntryFormData) => {
      addApplication({
        ...formData,
        resume: data.result?.resume || '',
      })
      setLogDialogOpen(false)
    },
    [addApplication, data.result?.resume],
  )

  React.useEffect(() => {
    if (!isStreaming && data?.result?.resume) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth', // Use 'auto' for instant scrolling
      })
    }
  }, [isStreaming, data])

  const handleUseResultResume = React.useCallback(
    (
      action:
        | 'restart-with-new-resume'
        | 'restart-with-old-resume'
        | 're-analyze',
    ) => {
      const file = new File([data.result?.resume || ''], downloadFilename, {
        type: 'text/markdown',
      })
      props.onFinished &&
        props.onFinished(action, {
          name: file.name,
          type: file.type,
          size: file.size,
          text: data.result?.resume || '',
        })
    },
    [props.onFinished, data.result?.resume, downloadFilename],
  )

  const stripYaml = React.useCallback((text: string) => {
    return text.replace(/^---\s*[\s\S]*?(?:---\s*|$)/, '')
  }, [])

  if (!data.result?.resume) return null
  return (
    <React.Fragment>
      {props.isStreaming ? (
        <div className="overflow-hidden bg-muted-foreground p-24">
          <div className="prose w-full max-w-full bg-white p-24">
            <Streamdown
              rehypePlugins={[rehypeRaw]}
              remarkPlugins={[remarkGfm, remarkFrontmatter, remarkBreaks]}
            >
              {stripYaml(data.result?.resume)}
            </Streamdown>
            {isStreaming && (
              <div
                id="resume-stream-cursor"
                className="h-1 w-full bg-red-500"
              />
            )}
            <Streamdown
              rehypePlugins={[rehypeRaw]}
              remarkPlugins={[remarkGfm, remarkFrontmatter, remarkBreaks]}
              components={{
                div: ({ children }) => (
                  <div className="text-muted-foreground/80">{children}</div>
                ),
                h2: ({ children }) => (
                  <h2 className="text-muted-foreground/80">{children}</h2>
                ),
                b: ({ children }) => (
                  <b className="text-muted-foreground/80">{children}</b>
                ),
                p: ({ children }) => (
                  <p className="text-muted-foreground/80">{children}</p>
                ),
                li: ({ children }) => (
                  <li className="text-muted-foreground/80">{children}</li>
                ),
                strong: ({ children }) => (
                  <strong className="text-muted-foreground/80">
                    {children}
                  </strong>
                ),
              }}
            >
              {data.input.resume
                ? stripYaml(data.input.resume)?.substring(
                    data.result?.resume?.length || 0,
                    data?.input.resume.length,
                  )
                : null}
            </Streamdown>
          </div>
        </div>
      ) : (
        <div className="space-y-8">
          <Tabs defaultValue={'after'}>
            <TabsList>
              <TabsTrigger value="before">Before</TabsTrigger>
              <TabsTrigger value="after">After</TabsTrigger>
            </TabsList>
            <TabsContent value="before">
              <div className="bg-muted-foreground p-24">
                <div className="prose w-full max-w-full bg-white p-24">
                  <Markdown
                    rehypePlugins={[rehypeRaw]}
                    remarkPlugins={[remarkGfm, remarkFrontmatter, remarkBreaks]}
                  >
                    {data.input?.resume}
                  </Markdown>
                </div>
              </div>
            </TabsContent>
            <TabsContent value="after" className="flex w-full flex-col">
              {mode === 'edit' ? (
                <MarkdownEditor
                  text={editText}
                  onTextChange={(text) => setEditText(text)}
                />
              ) : (
                <div className="overflow-hidden bg-muted-foreground p-24">
                  <div className="prose w-full max-w-full bg-white p-24">
                    {data.result?.resume ? (
                      <Markdown
                        rehypePlugins={[rehypeRaw]}
                        remarkPlugins={[
                          remarkGfm,
                          remarkFrontmatter,
                          remarkBreaks,
                        ]}
                      >
                        {data.result.resume}
                      </Markdown>
                    ) : (
                      <p>No resume content available.</p>
                    )}
                  </div>
                </div>
              )}
            </TabsContent>
          </Tabs>
        </div>
      )}
      {createPortal(
        <div className="flex w-full flex-row justify-between">
          {mode == 'edit' ? (
            <div className="flex w-full flex-row-reverse">
              <Button onClick={() => setMode('default')}>Cancel</Button>
              <Button
                onClick={() => {
                  setMode('default')
                  setData((prev) => ({
                    ...prev,
                    result: { ...prev.result, resume: editText },
                  }))
                }}
              >
                Save
              </Button>
            </div>
          ) : (
            <div className="flex flex-row items-center gap-2">
              <Button
                onClick={() => {
                  setMode('edit')
                  setEditText(data.result?.resume || '')
                }}
                disabled={isStreaming}
              >
                <EditIcon />
                Edit
              </Button>
              <ResumeDownloader
                isStreaming={isStreaming}
                resumeText={data.result?.resume || ''}
              />
              <DropdownMenu disabled={isStreaming}>
                <DropdownMenuTrigger
                  render={(props) => (
                    <Button {...props}>
                      <CheckIcon />
                      Next Steps
                    </Button>
                  )}
                />
                <DropdownMenuContent className={'min-w-fit'}>
                  <DropdownMenuGroup>
                    <DropdownMenuLabel>Track Application</DropdownMenuLabel>
                    <DropdownMenuItem
                      onClick={() => setLogDialogOpen(true)}
                      className="text-nowrap"
                    >
                      <ClipboardListIcon className="mr-2 h-4 w-4" />
                      Log Application
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuLabel>New Resume</DropdownMenuLabel>

                    <DropdownMenuItem
                      closeOnClick={false}
                      onClick={() => {
                        handleUseResultResume('re-analyze')
                      }}
                      className="text-nowrap"
                    >
                      {props.isAnalyzing && (
                        <Loader2Icon className="animate-spin" />
                      )}
                      Re-analyze {'(same job)'}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      closeOnClick={false}
                      onClick={() => {
                        handleUseResultResume('restart-with-new-resume')
                      }}
                      className="text-nowrap"
                    >
                      Restart {'(new job)'}
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                  <DropdownMenuSeparator />
                  <DropdownMenuGroup>
                    <DropdownMenuLabel>Old Resume</DropdownMenuLabel>
                    <DropdownMenuItem
                      closeOnClick={false}
                      onClick={() => {
                        handleUseResultResume('restart-with-old-resume')
                      }}
                      className="text-nowrap"
                    >
                      Restart {'(new job)'}
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}
        </div>,
        document.getElementById('control-bar-step-action') as Element,
      )}
      <JobEntryDialog
        open={logDialogOpen}
        onOpenChange={setLogDialogOpen}
        onSave={handleLogApplication}
        title="Log Job Application"
        description="Track this application in your job log"
        initialData={{
          resume: data.result?.resume || '',
          date: new Date().toISOString().split('T')[0],
        }}
      />
    </React.Fragment>
  )
}

const MarkdownEditor = (props: {
  text: string
  onTextChange?: (text: string) => void
}) => {
  const { text, onTextChange } = props
  const [previewWrap, setPreviewWrap] = React.useState(false)

  return (
    <div className="flex w-full flex-col">
      <ResizablePanelGroup direction="horizontal">
        <ResizablePanel>
          <div className="h-full">
            <Suspense
              fallback={
                <div className="flex h-full items-center justify-center">
                  <Loader2Icon className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              }
            >
              <MonacoEditor
                height="100%"
                defaultLanguage="markdown"
                value={text}
                onChange={(value) =>
                  onTextChange && onTextChange(value as string)
                }
                theme="vs-dark" // or "light"
                options={{
                  minimap: { enabled: false },
                  fontSize: 14,
                  wordWrap: 'on',
                  lineNumbers: 'on',
                  scrollBeyondLastLine: false,
                  automaticLayout: true, // Crucial for ResizablePanels
                  padding: { top: 16, bottom: 16 },
                  fontFamily: 'var(--font-mono)', // Use your project's mono font
                  glyphMargin: false,
                  folding: true,
                }}
              />
            </Suspense>
          </div>
        </ResizablePanel>
        <ResizableHandle className="mx-6 w-1" withHandle />
        <ResizablePanel>
          <div className="relative bg-muted-foreground">
            <div className="absolute top-0 left-0">
              <Button onClick={() => setPreviewWrap((prev) => !prev)}>
                Toggle Wrap
              </Button>
            </div>
            <div
              className={cn(
                'prose bg-white p-8 shadow-lg',
                previewWrap ? 'w-full max-w-none' : 'w-274 min-w-274',
              )}
            >
              <Markdown
                rehypePlugins={[rehypeRaw]}
                remarkPlugins={[remarkGfm, remarkFrontmatter, remarkBreaks]}
              >
                {text}
              </Markdown>
            </div>
          </div>
        </ResizablePanel>
      </ResizablePanelGroup>
    </div>
  )
}
